import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Loader2, Pin, Table2, X } from "lucide-react";
import { saveAs } from "file-saver";
import api, { baseURL } from "~/services/api";
import { receivableStatusesOptions } from "~/functions/selectPopoverOptions";
import ChartOfAccountsModal from "../ChartOfAccountsModal";
import { toast } from "react-toastify";
import NewDatepicker from "./NewDatepicker";

const STORAGE_KEY = "payees_pinned_filters";
const STORAGE_PINNED_KEY = "payees_filters_pinned";

const EMPTY_FILTERS = {
  status: "",
  showSummary: false,
  chartOfAccount: "",
  entryDateFrom: "",
  entryDateTo: "",
  dueDateFrom: "",
  dueDateTo: "",
  settlementFrom: "",
  settlementTo: "",
  cashDateFrom: "",
  cashDateTo: "",
  paymentMethodFromSettlement: false,
  paymentMethodId: "",
};

// Backwards-compatible alias for the existing initial-form object.
const DEFAULT_FORM = EMPTY_FILTERS;

export function getStoredFilters() {
  try {
    const pinned = localStorage.getItem(STORAGE_PINNED_KEY) === "true";
    if (!pinned) return null;
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const isIsoDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value || "");
const isUsDate = (value) => /^\d{2}\/\d{2}\/\d{4}$/.test(value || "");

const usToIsoDate = (value) => {
  if (!value) return "";
  if (isIsoDate(value)) return value;
  if (!isUsDate(value)) return "";

  const [month, day, year] = value.split("/").map(Number);
  const date = new Date(year, month - 1, day);
  const isValid =
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day;

  if (!isValid) return "";

  const mm = String(month).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return `${year}-${mm}-${dd}`;
};

const DateInputWithClear = ({ label, value, onChange, readOnly = false }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[10px] font-bold text-gray-400 uppercase">
      {label}
    </label>
    <div className="relative w-full">
      <NewDatepicker
        value={value}
        onChange={(nextValue) => onChange({ target: { value: nextValue } })}
        disabled={readOnly}
        className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm pr-10 ${
          readOnly
            ? "text-gray-400 bg-gray-100 cursor-not-allowed"
            : "text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-500"
        }`}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange({ target: { value: "" } })}
          disabled={readOnly}
          className="absolute top-1/2 right-2 transform -translate-y-1/2 text-gray-400 hover:text-red-500 p-1 rounded-full transition-colors bg-white hover:bg-gray-100"
          title="Clear date"
        >
          <X size={14} />
        </button>
      )}
    </div>
  </div>
);

const SearchableSelect = ({
  label,
  options,
  value,
  placeholder = "Select an option",
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState(
    options.find((opt) => opt === value)?.toString() ||
      options.find((opt) => opt.value === value)?.label ||
      options.find((opt) => opt.label === value)?.label ||
      value ||
      ""
  );
  const containerRef = useRef(null);

  useEffect(() => {
    const next =
      options.find((opt) => opt === value)?.toString() ||
      options.find((opt) => opt.value === value)?.label ||
      options.find((opt) => opt.label === value)?.label ||
      value ||
      "";
    setQuery(next);
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const normalizedOptions = options.map((opt) =>
    typeof opt === "string" ? { value: opt, label: opt } : opt
  );

  const filteredOptions = normalizedOptions.filter((opt) =>
    String(opt.label ?? "").toLowerCase().includes(String(query ?? "").toLowerCase())
  );

  const handleSelect = (option) => {
    setQuery(option.label);
    onChange(option.value);
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    onChange(e.target.value);
    setIsOpen(true);
  };

  return (
    <div className="flex flex-col gap-1.5" ref={containerRef}>
      <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">
        {label}
      </label>
      <div className="relative">
        <input
          type="text"
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-500 pr-8"
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
        />
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          <ChevronDown
            size={16}
            className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
          />
        </button>

        {isOpen && (
          <div className="absolute z-50 mt-1 w-full bg-white shadow-xl max-h-48 rounded-md py-1 text-sm overflow-auto border border-gray-200 top-full left-0">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-2 text-gray-500 italic">No options found</div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelect(option)}
                  className={`w-full text-left px-4 py-2 hover:bg-orange-50 flex items-center justify-between ${
                    option.value === value
                      ? "bg-orange-50 text-orange-700 font-medium"
                      : "text-gray-700"
                  }`}
                >
                  {option.label}
                  {option.value === value && <Check size={14} />}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default function ModalFilters({
  opened = false,
  onClose = () => null,
  applying = false,
  onApplyFilters = () => null,
}) {
  const [formData, setFormData] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed?.formData
        ? { ...EMPTY_FILTERS, ...parsed.formData }
        : EMPTY_FILTERS;
    } catch {
      return EMPTY_FILTERS;
    }
  });
  const [pinned, setPinned] = useState(
    () => localStorage.getItem(STORAGE_PINNED_KEY) === "true"
  );
  const [isClosing, setIsClosing] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [chartOfAccountOptions, setChartOfAccountOptions] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [selectedAccounts, setSelectedAccounts] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed?.selectedAccounts)
        ? parsed.selectedAccounts
        : [];
    } catch {
      return [];
    }
  });

  const hasEntryDate = Boolean(formData.entryDateFrom || formData.entryDateTo);
  const hasDueDate = Boolean(formData.dueDateFrom || formData.dueDateTo);
  const hasSettlementDate = Boolean(
    formData.settlementFrom || formData.settlementTo
  );

  useEffect(() => {
    if (opened) {
      setIsClosing(false);
      api.get("/paymentmethods/options")
        .then(({ data }) => setPaymentMethods(data || []))
        .catch(() => toast.error("Failed to load payment methods."));
    }
  }, [opened]);

  const handleChange = (field, value) => {
    const groupByField = {
      entryDateFrom: "entry",
      entryDateTo: "entry",
      dueDateFrom: "due",
      dueDateTo: "due",
      settlementFrom: "settlement",
      settlementTo: "settlement",
    };

    setFormData((prev) => {
      const group = groupByField[field];
      if (!group) {
        return { ...prev, [field]: value };
      }

      const next = { ...prev, [field]: value };
      if (!value) {
        return next;
      }

      if (group !== "entry") {
        next.entryDateFrom = "";
        next.entryDateTo = "";
      }
      if (group !== "due") {
        next.dueDateFrom = "";
        next.dueDateTo = "";
      }
      if (group !== "settlement") {
        next.settlementFrom = "";
        next.settlementTo = "";
      }

      return next;
    });
  };

  // Param-key map for persisted filters (must match buildQueryString keys and
  // the index.js loader's filtersParams handling).
  const buildPersistedFilters = () => ({
    status: formData.status || "null",
    show_summary: formData.showSummary ? "true" : "null",
    entry_date_from: usToIsoDate(formData.entryDateFrom) || "null",
    entry_date_to: usToIsoDate(formData.entryDateTo) || "null",
    due_date_from: usToIsoDate(formData.dueDateFrom) || "null",
    due_date_to: usToIsoDate(formData.dueDateTo) || "null",
    settlement_from: usToIsoDate(formData.settlementFrom) || "null",
    settlement_to: usToIsoDate(formData.settlementTo) || "null",
    cash_date_from: usToIsoDate(formData.cashDateFrom) || "null",
    cash_date_to: usToIsoDate(formData.cashDateTo) || "null",
    payment_method_from_settlement: formData.paymentMethodFromSettlement ? "true" : "null",
    paymentmethod_id: formData.paymentMethodId || "null",
  });

  const persistFilters = () => {
    const payload = {
      formData,
      selectedAccounts,
      filters: buildPersistedFilters(),
      chartCodes: selectedAccounts.map((acc) => acc.code),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  };

  const requireValidPaymentMethod = () => {
    if (!formData.paymentMethodId || paymentMethods.some((method) => method.id === formData.paymentMethodId)) return true;
    toast.error("Select a payment method from the list.");
    return false;
  };

  const handleTogglePin = () => {
    if (!pinned && !requireValidPaymentMethod()) return;
    const next = !pinned;
    setPinned(next);
    localStorage.setItem(STORAGE_PINNED_KEY, String(next));
    if (!next) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      persistFilters();
    }
  };

  const buildQueryString = () => {
    const params = new URLSearchParams();
    params.set("limit", "50");
    params.set("page", "1");
    params.set("orderBy", "due_date");
    params.set("orderASC", "ASC");
    params.set("search", "");
    params.set("status", formData.status || "null");
    if (formData.showSummary) params.set("show_summary", "true");
    params.set("entry_date_from", usToIsoDate(formData.entryDateFrom) || "null");
    params.set("entry_date_to", usToIsoDate(formData.entryDateTo) || "null");
    params.set("due_date_from", usToIsoDate(formData.dueDateFrom) || "null");
    params.set("due_date_to", usToIsoDate(formData.dueDateTo) || "null");
    params.set("settlement_from", usToIsoDate(formData.settlementFrom) || "null");
    params.set("settlement_to", usToIsoDate(formData.settlementTo) || "null");
    params.set("cash_date_from", usToIsoDate(formData.cashDateFrom) || "null");
    params.set("cash_date_to", usToIsoDate(formData.cashDateTo) || "null");
    params.set("payment_method_from_settlement", String(Boolean(formData.paymentMethodFromSettlement)));
    params.set("paymentmethod_id", formData.paymentMethodId || "null");

    const hasChartCodes = selectedAccounts && selectedAccounts.length > 0;
    const endpoint = hasChartCodes ? "/payee/filter" : "/payee";

    return `${endpoint}?${params.toString()}`;
  };

  const handleApply = () => {
    if (!requireValidPaymentMethod()) return;
    const query = buildQueryString();
    const chartCodes = selectedAccounts.map(acc => acc.code);
    if (pinned) {
      persistFilters();
    }
    console.log("Payees filters query:", query);
    console.log("Chart codes:", chartCodes);
    onApplyFilters(query, chartCodes);
    handleClose();
  };

  const handleClear = () => {
    setFormData(EMPTY_FILTERS);
    setSelectedAccounts([]);
    localStorage.removeItem(STORAGE_KEY);
  };

  const handleExportExcel = async () => {
    if (!requireValidPaymentMethod()) return;
    setExportingExcel(true);
    try {
      const payload = {
        entry_date_from: usToIsoDate(formData.entryDateFrom) || null,
        entry_date_to: usToIsoDate(formData.entryDateTo) || null,
        due_date_from: usToIsoDate(formData.dueDateFrom) || null,
        due_date_to: usToIsoDate(formData.dueDateTo) || null,
        settlement_from: usToIsoDate(formData.settlementFrom) || null,
        settlement_to: usToIsoDate(formData.settlementTo) || null,
        cash_date_from: usToIsoDate(formData.cashDateFrom) || null,
        cash_date_to: usToIsoDate(formData.cashDateTo) || null,
        payment_method_from_settlement: Boolean(formData.paymentMethodFromSettlement),
        paymentmethod_id: formData.paymentMethodId || null,
        status: formData.status || null,
      };

      if (selectedAccounts && selectedAccounts.length > 0) {
        payload.chart_codes = selectedAccounts.map(acc => acc.code);
      }

      console.log("Exporting Excel with filters:", payload);

      const { data } = await api.post("/payee/excel", payload);

      const downloadUrl = `${baseURL}/get-file/${data.name}`;
      saveAs(downloadUrl, `${data.name}.xlsx`);

      console.log("Excel exported successfully:", data.name);
    } catch (err) {
      console.error("Error exporting Excel:", err);
      toast(err?.response?.data?.error || "Failed to export Excel", {
        type: "error",
        autoClose: 3000,
      });
    } finally {
      setExportingExcel(false);
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 250);
  };

  if (!opened) return null;

  return (
    <>
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes slideOutRight {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(100%);
            opacity: 0;
          }
        }

        .modal-slide-in {
          animation: slideInRight 0.3s ease-out forwards;
        }

        .modal-slide-out {
          animation: slideOutRight 0.3s ease-in forwards;
        }
      `}</style>

      <div
        className="fixed inset-0 z-50 flex justify-end items-center py-6"
        style={{ backgroundColor: "rgba(0,0,0,.75)" }}
        onClick={(event) => {
          if (event.target === event.currentTarget) handleClose();
        }}
      >
        <div
          className={`bg-white w-full max-w-md h-full flex flex-col shadow-2xl overflow-hidden rounded-l-2xl ${
            isClosing ? "modal-slide-out" : "modal-slide-in"
          }`}
        >
          <div className="flex items-center justify-between px-6 py-6 border-b border-gray-200 bg-white shrink-0">
            <div className="flex flex-col gap-1">
              <h2 className="text-lg font-bold text-gray-900 uppercase tracking-tight">
                Filters
              </h2>
              <p className="text-xs text-gray-500 font-medium">
                Refine your search results
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleTogglePin}
                title={pinned ? "Unpin filters" : "Pin filters"}
                className={`p-2 rounded-md transition-colors ${
                  pinned
                    ? "bg-orange-100 text-orange-600 hover:bg-orange-200"
                    : "hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                }`}
              >
                <Pin size={18} className={pinned ? "fill-orange-500" : ""} />
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="p-2 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-6 text-sm text-gray-700 custom-scrollbar">
            <div className="flex flex-col gap-6">
              <div className="grid grid-cols-2 gap-3">
                <DateInputWithClear
                  label="Entry Date From"
                  value={formData.entryDateFrom}
                  readOnly={hasDueDate || hasSettlementDate}
                  onChange={(e) => handleChange("entryDateFrom", e.target.value)}
                />
                <DateInputWithClear
                  label="Entry Date To"
                  value={formData.entryDateTo}
                  readOnly={hasDueDate || hasSettlementDate}
                  onChange={(e) => handleChange("entryDateTo", e.target.value)}
                />
                <DateInputWithClear
                  label="Due Date From"
                  value={formData.dueDateFrom}
                  readOnly={hasEntryDate || hasSettlementDate}
                  onChange={(e) => handleChange("dueDateFrom", e.target.value)}
                />
                <DateInputWithClear
                  label="Due Date To"
                  value={formData.dueDateTo}
                  readOnly={hasEntryDate || hasSettlementDate}
                  onChange={(e) => handleChange("dueDateTo", e.target.value)}
                />
                <DateInputWithClear
                  label="Settlement Date From"
                  value={formData.settlementFrom}
                  readOnly={hasEntryDate || hasDueDate}
                  onChange={(e) =>
                    handleChange("settlementFrom", e.target.value)
                  }
                />
                <DateInputWithClear
                  label="Settlement Date To"
                  value={formData.settlementTo}
                  readOnly={hasEntryDate || hasDueDate}
                  onChange={(e) => handleChange("settlementTo", e.target.value)}
                />
                <DateInputWithClear label="Cash Date From" value={formData.cashDateFrom}
                  onChange={(e) => handleChange("cashDateFrom", e.target.value)} />
                <DateInputWithClear label="Cash Date To" value={formData.cashDateTo}
                  onChange={(e) => handleChange("cashDateTo", e.target.value)} />

              </div>

              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                <input type="checkbox" checked={Boolean(formData.paymentMethodFromSettlement)}
                  onChange={(event) => handleChange("paymentMethodFromSettlement", event.target.checked)}
                  className="accent-orange-500" />
                Use payment method from Settlement
              </label>
              <SearchableSelect label="Payment Method"
                options={paymentMethods.map((method) => ({ value: method.id, label: method.description }))}
                value={formData.paymentMethodId || ""}
                onChange={(value) => handleChange("paymentMethodId", value)} />
              <SearchableSelect
                label="Status"
                options={receivableStatusesOptions}
                value={formData.status}
                onChange={(val) => handleChange("status", val)}
              />

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Chart of Account
                </label>
                <button
                  type="button"
                  onClick={() => setChartModalOpen(true)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-left hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  {selectedAccounts.length > 0 ? (
                    <span className="text-gray-800">
                      {selectedAccounts.length} account(s) selected
                    </span>
                  ) : (
                    <span className="text-gray-400">Select accounts starting with "02"</span>
                  )}
                </button>
              </div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={Boolean(formData.showSummary)}
                  onChange={(event) => handleChange("showSummary", event.target.checked)}
                  className="accent-orange-500 cursor-pointer"
                />
                Show summary for all filtered payees
              </label>
            </div>
          </div>

          <div className="px-6 py-6 border-t border-gray-200 bg-gray-50 shrink-0 flex flex-col gap-3">
            <button
              type="button"
              onClick={handleExportExcel}
              className="w-full py-2 text-sm font-medium text-white bg-green-600 border border-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              disabled={exportingExcel}
            >
              {exportingExcel ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Table2 size={16} />
                  Export Excel
                </>
              )}
            </button>
            <button
              type="button"
              onClick={handleClear}
              className="w-full py-2 text-sm font-medium text-red-600 bg-white border border-red-100 rounded-lg hover:bg-red-50 transition-colors"
            >
              Clean All Filters
            </button>
            <div className="flex gap-3 w-full">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApply}
                disabled={applying}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              >
                {applying && <Loader2 size={16} className="animate-spin" />}
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      <ChartOfAccountsModal
        isOpen={chartModalOpen}
        onClose={() => setChartModalOpen(false)}
        selectedAccounts={selectedAccounts}
        onApply={(accounts) => {
          setSelectedAccounts(accounts);
          setChartModalOpen(false);
        }}
      />
    </>
  );
}
